// Keep failed actions visible even when a caller/adapter resolves a 4xx response.
export function readMissedStatusList(response) {
  if (response?.status !== 200 || !Array.isArray(response.data) || response.data.some(check =>
    !check || !Number.isSafeInteger(check.id) || check.id <= 0 || !Number.isSafeInteger(check.incidentId) ||
    typeof check.rangerId !== 'string')) throw new Error('Status-alert refresh was incomplete; previous alerts are retained.');
  return response.data;
}

export async function acknowledgeStatusCheck({ api, incidentId, currentSession, apply }) {
  const session = currentSession();
  if (!session || !incidentId) return false;
  const response = await api.post(`/incidents/${incidentId}/status-check/ack`, undefined, { timeout: 15000 });
  const iso = response?.data?.statusCheckAcknowledgedUtc, timestamp = Date.parse(iso);
  if (response?.status !== 200 || !Number.isFinite(timestamp)) throw new Error('Status-check confirmation was not received.');
  if (currentSession() !== session) return false;
  apply({ iso, timestamp });
  return true;
}

export async function submitMissedStatusAction(api, route, body) {
  const response = await api.post(route, body, { timeout: 15000 });
  if (response?.status !== 200) {
    const error = new Error(response?.data?.message || (typeof response?.data === 'string' ? response.data : 'Status-check action was not accepted.'));
    error.response = response;
    throw error;
  }
  const data = response.data;
  if (!data || typeof data !== 'object' ||
      (route.endsWith('/take-responsibility') && (!Number.isSafeInteger(data.id) || !Number.isSafeInteger(data.responsibleUserId))) ||
      (route.endsWith('/resolve') && data.success !== true)) throw new Error('Status-check action receipt was not confirmed.');
  return data;
}

// Deduplicate only one account/session and acknowledgment cycle. Failure is
// retryable after a short cooldown, including 4xx returned as fulfilled promises.
export function createMissedCheckReporter({ api, currentSession, now = Date.now, retryMs = 10000 }) {
  const records = new Map();
  return {
    clear(incidentId, rangerId) {
      const ranger = String(rangerId || '').trim().toUpperCase();
      for (const [key, record] of records) if (String(record.incidentId) === String(incidentId) && record.ranger === ranger) records.delete(key);
    },
    report(incidentId, rangerId, cycle) {
      const session = currentSession(), ranger = String(rangerId || '').trim().toUpperCase();
      if (!session || !incidentId || !ranger) return Promise.resolve({ skipped: true });
      const key = JSON.stringify([session, incidentId, ranger, cycle || 'initial']);
      let record = records.get(key);
      if (record?.pending) return record.pending;
      if (record?.accepted || record?.retryAt > now()) return Promise.resolve({ skipped: true });
      record = { incidentId, ranger, accepted: false, retryAt: 0 };
      records.set(key, record);
      record.pending = Promise.resolve().then(async () => {
        if (currentSession() !== session) return { skipped: true };
        const observed = Date.parse(cycle);
        const data = await submitMissedStatusAction(api, '/missedstatuschecks/report', {
          incidentId, rangerId, ...(Number.isFinite(observed) ? { observedCycleUtc: new Date(observed).toISOString() } : {}),
        });
        if (!Number.isSafeInteger(data?.id) || data.id <= 0) throw new Error('Status-check receipt was not confirmed.');
        if (currentSession() === session) record.accepted = true;
        return data;
      }).catch(error => { record.retryAt = now() + retryMs; throw error; }).finally(() => {
        record.pending = null;
        if (currentSession() !== session && records.get(key) === record) records.delete(key);
      });
      return record.pending;
    },
  };
}
